import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  useDeleteCartItemMutation,
  useGetCartQuery,
  useGetProfileQuery,
} from '../../apis/mlkApi/mlk.api.hooks';
import { parseRes } from '../../apis/utils';
import { Button } from '../../components/Button/Button';
import { EmptyState } from '../../components/EmptyState/EmptyState';
import { EmptyStateType } from '../../components/EmptyState/emptyStateHelper';
import { BaseLayout } from '../../components/Layout/Layout';
import { SeparatorLine } from '../../components/SeparatorLine/SeparatorLine';
import { useBack } from '../../hooks/useBack';
import { useLanguage } from '../../locale/useLanguage';
import { Product } from '../../models';
import {
  CartElement as CartElementModel,
  PdrConfiguration,
} from '../../models/payment.models';
import { Routes } from '../../routes/types';

import './CartView.css';
import { CartElement } from './components/CartElement/CartElement';
import { ModalRef } from '../../components/Modal/Modal';
import { PdrHoursModal } from './components/PdrHoursModal/PdrHoursModal';

type CartViewProps = {};

export const CartView: React.FC<CartViewProps> = () => {
  const [t] = useLanguage();
  const history = useHistory();
  const goBack = useBack();

  const { data: profile } = useGetProfileQuery(undefined);
  const { data: cart } = useGetCartQuery(undefined);
  const [removeItemApiCall] = useDeleteCartItemMutation();

  const [hoursPdr, setHoursPdr] = useState<string>();
  const modalRef = useRef<ModalRef>(null);

  const goToHome = useCallback(() => {
    history.push(Routes.Home);
  }, [history]);

  const closeCta = useMemo(() => {
    return <Button label={t('cart.emptyStateCta')} onClick={goToHome} />;
  }, [goToHome, t]);

  const goToEditProfile = useCallback(() => {
    history.push(Routes.CompleteUserInfo);
  }, [history]);

  const goToPaymentMethod = useCallback(() => {
    history.push(Routes.PaymentMethod);
  }, [history]);

  const removeItem = useCallback(
    (itemId: number) => {
      return () =>
        removeItemApiCall(itemId).then((res) => {
          const { errorRes } = parseRes(res);
          if (errorRes.error) {
            // TODO: Handle error
          }
        });
    },
    [removeItemApiCall]
  );

  const handleClickPdrModal = (config: PdrConfiguration) => {
    setHoursPdr(config.pdr?.orari);
    modalRef.current?.openModal();
  };

  const onEdit = useCallback(
    (cartElement: CartElementModel) => {
      return () => {
        if (cartElement.product === Product.Pdr) {
          history.push(Routes.SelectPickUpPoint, {
            configuration: cartElement.configuration,
          });
        } else {
          history.push(Routes.ChooseDeliveryTime, {
            cartItemInEdit: cartElement,
          });
        }
      };
    },
    [history]
  );

  const isEmptyStateVisible = cart && cart.items.length === 0;

  return (
    <BaseLayout
      isGreyBg={isEmptyStateVisible}
      hasViewportHeight={isEmptyStateVisible}
      hasBack
      onBackClick={goBack}
      title={t('pageTitles.cart')}
    >
      {isEmptyStateVisible && (
        <EmptyState type={EmptyStateType.Cart} cta={closeCta} />
      )}
      {cart && cart.items.length > 0 && profile && (
        <>
          <h2 className="cart--total">
            {t('cart.total')} {cart.total.toFixed(2)}€
          </h2>
          <p>{t('cart.totalDescription')}</p>
          <div className="cart--separator">
            <SeparatorLine />
          </div>
          {cart.items.map((item) => {
            const isPdr = item.product === Product.Pdr;
            return (
              <CartElement
                key={item.id}
                item={item}
                originalPrice={item.originalPrice.toFixed(2)}
                price={item.price.toFixed(2)}
                onDelete={removeItem(item.id)}
                actions={[
                  <a onClick={onEdit(item)} className="link">
                    {t('cart.edit')}
                  </a>,
                  <>
                    {isPdr && (
                      <a
                        onClick={() => handleClickPdrModal(item.configuration)}
                        className="link"
                      >
                        {t('cart.openingHours')}
                      </a>
                    )}
                  </>,
                ]}
              />
            );
          })}
          <div className="cart--billing-address-container">
            <h3 className="cart--billing-address-title">
              {t('cart.billingData')}
            </h3>
            <p>
              {profile.name} {profile.surname}
            </p>
            <p>{profile.residentialAddress}</p>
            <div className="cart--edit ">
              <a onClick={goToEditProfile} className="link">
                {t('cart.edit')}
              </a>
            </div>
          </div>
          <Button
            isDisabled={cart.items.length <= 0}
            onClick={goToPaymentMethod}
            isPrimary
            label={t('cart.continue')}
          />
          <div className="cart--back-button">
            <Button onClick={goBack} label={t('cart.back')} />
          </div>
        </>
      )}
      <PdrHoursModal hoursPdr={hoursPdr || ''} ref={modalRef} />
    </BaseLayout>
  );
};
